const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = `${BACKEND_URL}/api`;
const WHMCS_BASE = process.env.REACT_APP_WHMCS_URL || 'https://billing.drklynx.com';

// ── CORS-safe fetch wrapper ─────────────────────────────
// All external calls (billing.drklynx.com) go through our /api proxy
// to avoid browser CORS blocks. Direct calls to third-party origins
// are never made from client-side code.

async function safeFetch(url, opts = {}) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const res = await fetch(url, { signal: controller.signal, ...opts });
    clearTimeout(timeout);
    return res;
  } catch (err) {
    clearTimeout(timeout);
    if (err.name === 'AbortError') throw new Error('Request timed out');
    throw err;
  }
}

const api = {
  base: API_BASE,

  async get(path, opts = {}) {
    const res = await safeFetch(`${API_BASE}${path}`, {
      credentials: 'include',
      headers: { 'Accept': 'application/json', ...opts.headers },
      ...opts,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `GET ${path}: ${res.status}`);
    }
    return res.json();
  },

  async post(path, body, opts = {}) {
    const res = await safeFetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...opts.headers },
      credentials: 'include',
      body: JSON.stringify(body),
      ...opts,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `POST ${path}: ${res.status}`);
    }
    return res.json();
  },

  async put(path, body, opts = {}) {
    const res = await safeFetch(`${API_BASE}${path}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...opts.headers },
      credentials: 'include',
      body: JSON.stringify(body),
      ...opts,
    });
    if (!res.ok) throw new Error(`PUT ${path}: ${res.status}`);
    return res.json();
  },

  async del(path, opts = {}) {
    const res = await safeFetch(`${API_BASE}${path}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Accept': 'application/json', ...opts.headers },
      ...opts,
    });
    if (!res.ok) throw new Error(`DELETE ${path}: ${res.status}`);
    return res.json();
  },

  // ── WHMCS (proxied through /api/whmcs/* to avoid CORS) ──────
  // NEVER call billing.drklynx.com directly from browser.
  // All WHMCS calls route through our backend which proxies to WHMCS.
  // The backend adds WHMCS API credentials server-side.
  whmcs: {
    createOrder: (data) => api.post('/whmcs/order', data),
    getServices: (clientId) => api.get(`/whmcs/services/${clientId}`),
    getInvoices: (clientId) => api.get(`/whmcs/invoices/${clientId}`),
    provision: (data) => api.post('/whmcs/provision', data),
    // Commander: when wiring, proxy in backend via:
    //   httpx.AsyncClient().post(f"{WHMCS_BASE}/includes/api.php", data={...})
    // Never expose WHMCS API key or URL to frontend.
  },

  ledgerPulse: {
    getSeals: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return api.get(`/ledger-pulse/seals${qs ? '?' + qs : ''}`);
    },
    verify: (hash) => api.get(`/ledger-pulse/verify/${hash}`),
    connectStream: () => {
      const wsUrl = process.env.REACT_APP_LEDGER_WS || 'wss://core-01.bureau.drklynx.com/ledger/stream';
      const ws = new WebSocket(wsUrl);
      ws.onerror = () => console.warn('[LedgerPulse] WebSocket unavailable, falling back to polling');
      return ws;
    },
  },

  exodus: {
    recon: (data) => api.post('/exodus/recon', data),
    startMigration: (data) => api.post('/exodus/migrate', data),
    getStatus: (id) => api.get(`/exodus/status/${id}`),
    dryRun: (data) => api.post('/exodus/dry-run', data),
  },

  stripe: {
    createCheckout: (data) => api.post('/stripe/checkout', data),
  },

  status: {
    getTicker: () => api.get('/status/ticker').catch(() => ({ messages: [] })),
    getHealth: () => api.get('/status').catch(() => ({ overall: 'unknown' })),
  },

  metrics: {
    getHero: () => api.get('/metrics/hero').catch(() => ({
      nodes_online: 7, fleet_domains: 1500, attestations_24h: 847000, migrations_complete: 12400,
    })),
  },

  auth: {
    login: (data) => api.post('/auth/login', data),
    register: (data) => api.post('/auth/register', data),
    me: () => api.get('/auth/me'),
    logout: () => api.post('/auth/logout', {}),
    forgotPassword: (data) => api.post('/auth/forgot-password', data),
    resetPassword: (data) => api.post('/auth/reset-password', data),
  },

  blog: {
    getPosts: (params) => {
      const qs = new URLSearchParams(params).toString();
      return api.get(`/blog/posts${qs ? '?' + qs : ''}`);
    },
    getPost: (slug) => api.get(`/blog/posts/${slug}`),
    getTags: () => api.get('/blog/tags'),
  },

  contact: (data) => api.post('/contact', data),
  newsletter: (data) => api.post('/newsletter', data),
  ledgerPulseTelemetry: () => api.get('/ledger-pulse'),
  adminStats: () => api.get('/admin/stats'),
};

export default api;
